import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, Inbox, ShieldAlert, Trash2, UserRound, XCircle,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import StatusBadge from '../../components/StatusBadge';
import AuthMessage from '../../components/AuthMessage';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { DELETION_REQUEST_ERRORS, listDeletionRequests, resolveDeletionRequest } from '../../api/deletionRequests';
import { timeAgo } from '../../data/mockData';
import form from '../../styles/form.module.css';
import styles from './DeletionRequests.module.css';

const TABS = [
  { key: 'REQUESTED', label: 'Pending' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'CANCELLED', label: 'Cancelled' },
];

const EMPTY_COPY = {
  REQUESTED: 'Customers who ask to delete their account will show up here for review.',
  REJECTED: 'Requests you have rejected will appear here for reference.',
  CANCELLED: 'Requests the customer withdrew themselves will appear here for reference.',
};

function friendlyError(err) {
  return DELETION_REQUEST_ERRORS[err.message] || err.message || 'Something went wrong.';
}

function formatUsername(username) {
  if (!username) return 'Unknown customer';
  const digits = username.replace(/^p/i, '');
  if (/^91\d{10}$/.test(digits)) {
    const local = digits.slice(2);
    return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
  }
  return username;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function DeletionRequests() {
  const { user } = useAuth();
  const { notify } = useOrders();

  const [tab, setTab] = useState('REQUESTED');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rejectingSub, setRejectingSub] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectError, setRejectError] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const [approvingSub, setApprovingSub] = useState(null);
  const [approveAck, setApproveAck] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [approving, setApproving] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listDeletionRequests(tab, user.idToken);
      setRequests(list);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [tab, user.idToken]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const switchTab = (key) => {
    setTab(key);
    setRejectingSub(null);
    setApprovingSub(null);
  };

  const openReject = (sub) => {
    setRejectError('');
    setRejectNote('');
    setApprovingSub(null);
    setRejectingSub((current) => (current === sub ? null : sub));
  };

  const closeReject = () => {
    if (rejecting) return;
    setRejectingSub(null);
  };

  const submitReject = async (request) => {
    setRejecting(true);
    setRejectError('');
    try {
      await resolveDeletionRequest(request.sub, { status: 'REJECTED', note: rejectNote.trim() }, user.idToken);
      setRejectingSub(null);
      notify(`Rejected ${formatUsername(request.username)}'s deletion request`);
      await loadRequests();
    } catch (err) {
      setRejectError(friendlyError(err));
    } finally {
      setRejecting(false);
    }
  };

  const openApprove = (sub) => {
    setApproveError('');
    setApproveAck(false);
    setRejectingSub(null);
    setApprovingSub((current) => (current === sub ? null : sub));
  };

  const closeApprove = () => {
    if (approving) return;
    setApprovingSub(null);
  };

  const submitApprove = async (request) => {
    setApproving(true);
    setApproveError('');
    try {
      const result = await resolveDeletionRequest(request.sub, { status: 'APPROVED' }, user.idToken);
      setApprovingSub(null);
      const anonymized = result?.orders_anonymized;
      notify(
        `${formatUsername(request.username)}'s account was deleted`
        + (typeof anonymized === 'number' ? ` — ${anonymized} order${anonymized === 1 ? '' : 's'} anonymized` : ''),
      );
      await loadRequests();
    } catch (err) {
      setApproveError(friendlyError(err));
    } finally {
      setApproving(false);
    }
  };

  const isPending = tab === 'REQUESTED';

  return (
    <div>
      <PageHeader
        eyebrow="Admin · restricted"
        title="Account Deletion Requests"
        subtitle="Review customer requests to delete their account. Approving is permanent and cannot be undone."
      />

      <div className={styles.noticeBar}>
        <ShieldAlert size={15} />
        Approving deletes the customer&apos;s Cognito account, profile, addresses and invoices immediately. Orders are kept for GST but anonymized.
      </div>

      <div className={styles.filterRow}>
        <div className={styles.filterTabs}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${styles.filterTab} ${tab === key ? styles.filterTabActive : ''}`}
              onClick={() => switchTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className={styles.loadingText}>Loading requests…</p>}

      {!loading && error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load deletion requests"
          description={error}
          action={<Button variant="outline" onClick={loadRequests}>Retry</Button>}
        />
      )}

      {!loading && !error && requests.length === 0 && (
        <EmptyState
          icon={Inbox}
          title={`No ${TABS.find((t) => t.key === tab).label.toLowerCase()} requests`}
          description={EMPTY_COPY[tab]}
        />
      )}

      {!loading && !error && requests.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Reason</th>
                <th>Requested</th>
                <th>Status</th>
                <th>{isPending ? 'Actions' : 'Admin note'}</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.sub}>
                  <td>
                    <div className={styles.customerCell}>
                      <div className={styles.customerIcon}><UserRound size={16} /></div>
                      <div>
                        <p className={styles.customerName}>{formatUsername(r.username)}</p>
                        <p className={styles.customerSub} title={r.sub}>{r.sub}</p>
                      </div>
                    </div>
                  </td>
                  <td className={styles.reasonCell} title={r.reason}>
                    {r.reason || <span className={styles.muted}>No reason given</span>}
                  </td>
                  <td className={styles.timeCell} title={formatDate(r.createdAt)}>
                    {timeAgo(r.createdAt)}
                  </td>
                  <td><StatusBadge status={r.status} size="sm" /></td>
                  <td className={isPending ? styles.actionsCell : styles.noteCell}>
                    {isPending ? (
                      <div className={styles.actionsRow}>
                        <div className={styles.menuAnchor}>
                          <Button variant="outline" size="sm" icon={XCircle} onClick={() => openReject(r.sub)}>
                            Reject
                          </Button>
                          {rejectingSub === r.sub && (
                            <div className={styles.confirmPopover}>
                              <p className={styles.confirmTitle}>
                                <AlertTriangle size={14} /> Reject this request
                              </p>
                              <p className={styles.confirmText}>
                                {formatUsername(r.username)} keeps their account and can request deletion again later.
                                Your note is sent to them as an in-app notification.
                              </p>
                              <textarea
                                className={form.textarea}
                                placeholder="Optional note to the customer (e.g. outstanding refund pending)"
                                value={rejectNote}
                                onChange={(e) => setRejectNote(e.target.value)}
                                rows={3}
                                disabled={rejecting}
                              />
                              <AuthMessage>{rejectError}</AuthMessage>
                              <div className={styles.confirmActions}>
                                <Button variant="ghost" size="sm" onClick={closeReject} disabled={rejecting}>
                                  Cancel
                                </Button>
                                <Button variant="primary" size="sm" onClick={() => submitReject(r)} disabled={rejecting}>
                                  {rejecting ? 'Rejecting…' : 'Reject request'}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className={styles.menuAnchor}>
                          <Button variant="danger" size="sm" icon={Trash2} onClick={() => openApprove(r.sub)}>
                            Approve
                          </Button>
                          {approvingSub === r.sub && (
                            <div className={`${styles.confirmPopover} ${styles.dangerPopover}`}>
                              <p className={styles.confirmTitleDanger}>
                                <AlertTriangle size={14} /> Permanently delete this account
                              </p>
                              <p className={styles.confirmText}>
                                <strong>{formatUsername(r.username)}</strong> will be removed from Cognito
                                immediately. Profile, addresses, devices, invoices and coupon history are purged.
                                Orders are kept for GST but anonymized. This cannot be undone.
                              </p>
                              <label className={styles.ackRow}>
                                <input
                                  type="checkbox"
                                  checked={approveAck}
                                  onChange={(e) => setApproveAck(e.target.checked)}
                                  disabled={approving}
                                />
                                I understand this is irreversible
                              </label>
                              <AuthMessage>{approveError}</AuthMessage>
                              <div className={styles.confirmActions}>
                                <Button variant="ghost" size="sm" onClick={closeApprove} disabled={approving}>
                                  Cancel
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => submitApprove(r)}
                                  disabled={!approveAck || approving}
                                >
                                  {approving ? 'Deleting…' : 'Delete permanently'}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      r.note ? r.note : <span className={styles.muted}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
